/**
 * Dove finisce un lead nuovo quando chi lo crea non sceglie una pipeline.
 *
 * Fino al 15/09/2026 non finiva da nessuna parte: `pipeline_id` restava vuoto e il
 * lead spariva dal kanban, che mostra i lead per fase. Su NG Plast erano 159 lead su
 * 333 (segnalazione Fabio Bargero, MULTI S.R.L. creato e invisibile). Lo stesso
 * rimedio esisteva gia' nell'app mobile e nei lead da Meta, mai nel CRM da browser.
 *
 * Funzioni pure: la lettura dal database sta nel chiamante, qui c'e' solo la regola
 * di scelta, cosi' si prova senza database.
 */

export interface PipelineCandidate {
  id: string;
  is_active?: boolean | null;
  is_default?: boolean | null;
  /** Pipeline gestita dal sistema (es. "Prove gratuite" di Obly): mai come ripiego. */
  is_locked?: boolean | null;
  sort_order?: number | null;
}

export interface StageCandidate {
  id: string;
  pipeline_id: string;
  is_active?: boolean | null;
  sort_order?: number | null;
}

/** L'id della pipeline finta che in interfaccia raccoglie i lead senza pipeline. */
const VIRTUAL_LEGACY_ID = 'legacy';

const bySortOrder = (a: { sort_order?: number | null }, b: { sort_order?: number | null }) =>
  (a.sort_order ?? Number.MAX_SAFE_INTEGER) - (b.sort_order ?? Number.MAX_SAFE_INTEGER);

/**
 * La pipeline in cui mettere un lead nuovo: quella marcata come predefinita, altrimenti
 * la prima per ordinamento. Restituisce null se l'organizzazione non ne ha nessuna
 * utilizzabile: chi lavora con gli stati (Lovati, Diro Legno) non deve essere toccato.
 */
export function pickDefaultPipeline<T extends PipelineCandidate>(pipelines: T[]): T | null {
  const usable = pipelines
    .filter((p) => p.id !== VIRTUAL_LEGACY_ID && p.is_active !== false && !p.is_locked)
    .sort(bySortOrder);
  if (usable.length === 0) return null;
  return usable.find((p) => p.is_default === true) ?? usable[0];
}

/** La prima fase attiva della pipeline scelta. Senza fase il lead resterebbe comunque fuori dal kanban. */
export function pickFirstStage<T extends StageCandidate>(stages: T[], pipelineId: string): T | null {
  const usable = stages
    .filter((s) => s.pipeline_id === pipelineId && s.is_active !== false)
    .sort(bySortOrder);
  return usable[0] ?? null;
}

/**
 * Pipeline e fase da scrivere su un lead nuovo, o null se non c'e' niente da scrivere.
 * Si applica solo quando chi crea non ha scelto (una scelta esplicita, "Senza pipeline"
 * compreso, resta quella che e').
 */
export function resolveNewLeadPipeline(
  pipelines: PipelineCandidate[],
  stages: StageCandidate[],
): { pipeline_id: string; pipeline_stage_id: string | null } | null {
  const pipeline = pickDefaultPipeline(pipelines);
  if (!pipeline) return null;
  return {
    pipeline_id: pipeline.id,
    pipeline_stage_id: pickFirstStage(stages, pipeline.id)?.id ?? null,
  };
}
